import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { getAllCity } from '../../../FeatureRedux/citydetailReducer/getCityDetail';

function EditCityDetail() {
  const navigate = useNavigate();
  const location = useLocation();  
  const { cityname, statee, country } = location.state || {};  
  const dispatch = useDispatch();

  // Initialize with empty structure
  const [data, setData] = useState({
    message: "",
    data: {
      city: {
        cityname: "",
        country: "",
        state: "",
        latitude: 0,
        longitude: 0,
        imageUrl: "",
        description: ""
      },
      _id: "",
      history: [],
      transport: [],
      socialImpact: [],
      population: [],
      culture: [],
      economy: [],
      technology: [],
      createdAt: "",
      updatedAt: "",
      __v: 0
    }
  });

  const [activeTab, setActiveTab] = useState('basic');
  const [editingItem, setEditingItem] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const state = statee;
      if (cityname && state && country) {
        const resultAction = await dispatch(getAllCity({ cityname, state, country }));
        const citydata = resultAction.payload;
        if (citydata) {
          // Normalize data to ensure all expected fields exist
          const normalizedData = {
            message: citydata.message || "",
            data: {
              city: {
                cityname: citydata.data?.city?.cityname || "",
                country: citydata.data?.city?.country || "",
                state: citydata.data?.city?.state || "",
                latitude: citydata.data?.city?.latitude || 0,
                longitude: citydata.data?.city?.longitude || 0,
                imageUrl: citydata.data?.city?.imageUrl || "",
                description: citydata.data?.city?.description || ""
              },
              _id: citydata.data?._id || "",
              history: citydata.data?.history || [],
              transport: citydata.data?.transport || [],
              socialImpact: citydata.data?.socialImpact || [],
              population: citydata.data?.population || [],
              culture: citydata.data?.culture || [],
              economy: citydata.data?.economy || [],
              technology: citydata.data?.technology || [],
              createdAt: citydata.data?.createdAt || "",
              updatedAt: citydata.data?.updatedAt || "",
              __v: citydata.data?.__v || 0
            }
          };
          setData(normalizedData);
        }
        console.log("City Data:", citydata); 
      }
    };

    fetchData();
  }, [cityname, statee, country, dispatch]);

  // Toggle dark mode and save preference to localStorage
  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedMode);
    document.documentElement.classList.toggle('dark', savedMode);
  }, []);

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode.toString());
    document.documentElement.classList.toggle('dark', newMode);
  };

  // Handle image upload
  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    try {
      // In a real app, you would upload the file to your server here
      // This is a mock implementation that simulates upload
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create a mock URL for the uploaded image
      const mockUrl = URL.createObjectURL(file);

      if (editingSection) {
        // For array items
        setEditingItem(prev => ({
          ...prev,
          [field]: mockUrl
        }));
      } else {
        // For basic city info
        setData(prev => ({
          ...prev,
          data: {
            ...prev.data,
            city: {
              ...prev.data.city,
              [field]: mockUrl
            }
          }
        }));
      }
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Image upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setData(prev => ({
      ...prev,
      data: {
        ...prev.data,
        city: {
          ...prev.data.city,
          [name]: name === 'latitude' || name === 'longitude' ? parseFloat(value) : value
        }
      }
    }));
  };

  const handleArrayItemChange = (e) => {
    const { name, value } = e.target;
    setEditingItem(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveArrayItem = () => {
    setData(prev => {
      const newData = { ...prev };
      const section = [...newData.data[editingSection]];
      const index = section.findIndex(item => item._id === editingItem._id);

      if (index >= 0) {
        section[index] = editingItem;
      } else {
        section.push({ ...editingItem, _id: `new-${Date.now()}` });
      }

      newData.data[editingSection] = section;
      return newData;
    });

    setEditingItem(null);
    setEditingSection(null);
  };

  const deleteArrayItem = (section, id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      setData(prev => ({
        ...prev,
        data: {
          ...prev.data,
          [section]: prev.data[section].filter(item => item._id !== id)
        }
      }));
    }
  };

  const addNewItem = (section) => {
    const template = {
      history: { title: '', content: '', imageUrl: '' },
      transport: { type: '', details: '', imageUrl: '' },
      socialImpact: { aspect: '', impact: '', imageUrl: '' },
      population: { year: '', count: '', notes: '', imageUrl: '' },
      culture: { aspect: '', details: '', imageUrl: '' },
      economy: { sector: '', info: '', imageUrl: '' },
      technology: { field: '', description: '', imageUrl: '' }
    };

    setEditingItem(template[section]);
    setEditingSection(section);
  };

  const startEditing = (section, item) => {
    setEditingItem(item);
    setEditingSection(section);
  };

  const cancelEditing = () => {
    setEditingItem(null);
    setEditingSection(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the updated data to your API
    console.log("Updated data:", data);
    alert("Changes saved successfully!");
    navigate('/');
  };

  // Helper component for image upload/preview
  const ImageUploader = ({ url, onChange, fieldName }) => (
    <div className="mt-2" >
      {url && (
        <div className="mb-2 flex items-center space-x-2">
          <img
            src={url}
            alt="Preview"
            className="h-16 w-16 object-cover rounded"
          />
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
            {url.startsWith('blob:') ? 'New upload' : url}
          </span>
        </div>
      )}
      <label className="block">
        <span className="sr-only">Choose image</span>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => onChange(e, fieldName)}
          className="block w-full text-sm text-gray-500 dark:text-gray-400
            file:mr-4 file:py-2 file:px-4
            file:rounded-md file:border-0
            file:text-sm file:font-semibold
            file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100
            dark:file:bg-blue-900 dark:file:text-blue-100 dark:hover:file:bg-blue-800
            cursor-pointer"
        />
      </label>
      {uploading && (
        <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">Uploading...</div>
      )}
    </div>
  );

  return (
    <div className={` ${darkMode ? 'dark bg-gray-900' : 'bg-gray-50'}`} style={{
      overflowY: 'scroll',
      height: '80vh',
      width: '100%',
      scrollbarWidth: 'px',
      msOverflowStyle: 'none'
    }}>
      <div className="container mx-auto p-4 max-w-6xl">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Edit City Details: {data.data.city.cityname}
          </h1>
          <button
            onClick={toggleDarkMode}
            className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
            aria-label="Toggle dark mode"
          >
            {darkMode ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
            )}
          </button>
        </div>

        <div className={`rounded-lg shadow-md p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
            {['basic', 'history', 'transport', 'socialImpact', 'population', 'culture', 'economy', 'technology'].map(tab => (
              <button
                key={tab}
                className={`py-2 px-4 font-medium capitalize ${activeTab === tab
                  ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'socialImpact' ? 'Social Impact' : tab}
              </button>
            ))}
          </div>

          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">City Name</label>
                  <input
                    type="text"
                    name="cityname"
                    value={data.data.city.cityname}
                    onChange={handleBasicChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Country</label>
                  <input
                    type="text"
                    name="country"
                    value={data.data.city.country}
                    onChange={handleBasicChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">State/Province</label>
                  <input
                    type="text"
                    name="state"
                    value={data.data.city.state}
                    onChange={handleBasicChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Main Image</label>
                  <ImageUploader
                    url={data.data.city.imageUrl}
                    onChange={handleImageUpload}
                    fieldName="imageUrl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Latitude</label>
                  <input
                    type="number"
                    step="0.0001"
                    name="latitude"
                    value={data.data.city.latitude}
                    onChange={handleBasicChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Longitude</label>
                  <input
                    type="number"
                    step="0.0001"
                    name="longitude"
                    value={data.data.city.longitude}
                    onChange={handleBasicChange}
                    className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                <textarea
                  name="description"
                  value={data.data.city.description}
                  onChange={handleBasicChange}
                  rows={4}
                  className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          {/* Array Sections (History, Transport, etc.) */}
          {['history', 'transport', 'socialImpact', 'population', 'culture', 'economy', 'technology'].includes(activeTab) && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white capitalize">
                  {activeTab === 'socialImpact' ? 'Social Impact' : activeTab}
                </h2>
                <button
                  onClick={() => addNewItem(activeTab)}
                  className="bg-blue-500 hover:bg-blue-600 text-white py-1 px-3 rounded text-sm"
                >
                  Add New
                </button>
              </div>

              {/* Editing Form */}
              {editingSection === activeTab && (
                <div className={`p-4 rounded-lg mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h3 className={`font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {editingItem._id && !editingItem._id.startsWith('new') ? 'Edit Item' : 'Add New Item'}
                  </h3>

                  <div className="space-y-3">
                    {Object.keys(editingItem)
                      .filter(key => key !== '_id')
                      .map(key => (
                        <div key={key}>
                          <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} capitalize`}>
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </label>
                          {key === 'imageUrl' ? (
                            <ImageUploader
                              url={editingItem[key]}
                              onChange={handleImageUpload}
                              fieldName={key}
                            />
                          ) : key === 'content' || key === 'details' || key === 'impact' || key === 'info' || key === 'description' || key === 'notes' ? (
                            <textarea
                              name={key}
                              value={editingItem[key] || ''}
                              onChange={handleArrayItemChange}
                              rows={3}
                              className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                }`}
                            />
                          ) : (
                            <input
                              type={key === 'year' || key === 'count' ? 'number' : 'text'}
                              name={key}
                              value={editingItem[key] || ''}
                              onChange={handleArrayItemChange}
                              className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                                }`}
                            />
                          )}
                        </div>
                      ))}
                  </div>

                  <div className="flex justify-end space-x-2 mt-4">
                    <button
                      onClick={cancelEditing}
                      className={`px-3 py-1 border rounded text-sm ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-600' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveArrayItem}
                      className="bg-blue-500 hover:bg-blue-600 text-white py-1 px-3 rounded text-sm"
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}

              {/* List of Items */}
              <div className="space-y-4">
                {data.data[activeTab].length === 0 ? (
                  <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No items added yet.</p>
                ) : (
                  data.data[activeTab].map(item => (
                    <div
                      key={item._id}
                      className={`border rounded-lg p-4 hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'} ${darkMode ? 'border-gray-700' : 'border-gray-200'
                        }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex space-x-4">
                          {item.imageUrl && (
                            <img
                              src={item.imageUrl}
                              alt="Preview"
                              className="h-16 w-16 object-cover rounded"
                            />
                          )}
                          <div>
                            <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {item.title || item.type || item.aspect || item.year || item.sector || item.field}
                            </h3>
                            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {item.content || item.details || item.impact || item.info || item.description || item.notes}
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => startEditing(activeTab, item)}
                            className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-400 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteArrayItem(activeTab, item._id)}
                            className="text-red-500 hover:text-red-700 dark:hover:text-red-400 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              className={`px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditCityDetail;